import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, IconButton, useMediaQuery } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { Message, MessageTypeColor } from "../../../../types";
import { ReactionAndFavorite } from "../reaction-and-favorite/reaction-and-favorite";
import { CustomMarkdown } from "../../../custom-markdown/custom-markdown";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { useFlippingCardStyles } from "./styles";
import { useMessageInteractionTracking } from "../../../../hooks/useMessageInteractionTracking";
import { fixMalformedUnicodeText } from "../../../../utils/textSanitize";

interface FlippingCardMessageProps {
  message: Message;
  isBot: boolean;
  variant: MessageTypeColor;
  currentReaction?: string;
  isSaved?: boolean;
  markedIds: Record<string, string>;
  isTags?: string[];
  handleReactionClick: (reactionKey: string) => void;
  handleAddToFavorite: () => void;
  handleRemoveTag: (tag: string) => Promise<void>;
  setActiveFlippingCardText: React.Dispatch<React.SetStateAction<string>>;
  // When true, hide ReactionAndFavorite (used inside TabbedMessage)
  embedded?: boolean;
}

export const FlippingCardMessage: React.FC<FlippingCardMessageProps> = ({
  message,
  isBot,
  variant,
  currentReaction,
  isSaved,
  markedIds,
  isTags,
  handleReactionClick,
  handleAddToFavorite,
  handleRemoveTag,
  setActiveFlippingCardText,
  embedded = false,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const styles = useFlippingCardStyles(isBot, variant, embedded);
  const { trackInteraction } = useMessageInteractionTracking(message);

  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);

  const cards = useMemo(
    () =>
      (message.flipping_card ?? []).map((card) => ({
        ...card,
        ...(card.title !== undefined
          ? { title: fixMalformedUnicodeText(card.title) }
          : {}),
        front_text: fixMalformedUnicodeText(card.front_text),
        back_text: fixMalformedUnicodeText(card.back_text),
      })),
    [message.flipping_card],
  );

  useEffect(() => {
    if (cards.length === 0) {
      setActiveFlippingCardText("");
      return;
    }
    const currentCard = cards[currentCardIndex];
    if (!currentCard) {
      setActiveFlippingCardText("");
      return;
    }

    const displayedText = isFlipped
      ? currentCard.back_text
      : currentCard.front_text;
    const cardText = currentCard.title
      ? `${currentCard.title}\n${displayedText}`
      : displayedText;
    setActiveFlippingCardText(cardText);
  }, [
    currentCardIndex,
    isFlipped,
    cards,
    setActiveFlippingCardText,
  ]);

  if (cards.length === 0) {
    return null;
  }

  const currentCard = cards[currentCardIndex];
  const totalCards = cards.length;

  const handleCardClick = () => {
    setIsFlipped(!isFlipped);
    trackInteraction("flipping_card_flip");
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentCardIndex < totalCards - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
      setIsFlipped(false);
      trackInteraction("flipping_card_scroll");
    }
  };

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentCardIndex > 0) {
      setCurrentCardIndex(currentCardIndex - 1);
      setIsFlipped(false);
      trackInteraction("flipping_card_scroll");
    }
  };

  return (
    <Box sx={styles.container}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          perspective: "1000px",
          height: isSmallScreen ? "250px" : "300px",
          overflow: "visible",
        }}
      >
        <motion.div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            transformStyle: "preserve-3d",
            transformOrigin: "center center",
          }}
          animate={{ rotateY: isFlipped ? 180 : 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
        >
          {/* Front side */}
          <Box
            sx={{
              ...styles.cardSide,
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
            }}
            onClick={handleCardClick}
          >
            {currentCard.title && (
              <Typography
                variant="h6"
                sx={{
                  ...styles.title,
                }}
              >
                {currentCard.title}
              </Typography>
            )}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                overflow: "auto",
                padding: 1,
                boxSizing: "border-box",
                ...(embedded && {
                  [theme.breakpoints.down("sm")]: { padding: 0.5 },
                }),
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  ...styles.text,
                  textAlign: "center",
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 700,
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  maxWidth: "100%",
                }}
              >
                {currentCard.front_text}
              </Typography>
            </Box>
          </Box>

          {/* Back side */}
          <Box
            sx={{
              ...styles.cardSideBack,
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
            onClick={handleCardClick}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                overflow: "auto",
                padding: 1,
                boxSizing: "border-box",
                ...(embedded && {
                  [theme.breakpoints.down("sm")]: { padding: 0.5 },
                }),
              }}
            >
              <Box
                sx={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  width: "100%",
                  "& h1, & h2, & h3, & h4, & h5, & h6, & p, & span, & strong, & em, & ul, & ol, & li":
                    {
                      fontWeight: 500,
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      textAlign: "center",
                      fontSize: "20px",
                      lineHeight: 1.5,
                      color: "#000000 !important",
                      [theme.breakpoints.down("sm")]: {
                        fontSize: embedded ? "13px" : "14px",
                        lineHeight: 1.45,
                      },
                    },
                }}
              >
                <CustomMarkdown
                  components={{
                    p: ({ children, ...props }) => (
                      <p style={{ color: "#000000", margin: 0 }} {...props}>
                        {children}
                      </p>
                    ),
                    span: ({ children, ...props }) => (
                      <span style={{ color: "#000000" }} {...props}>
                        {children}
                      </span>
                    ),
                    strong: ({ children, ...props }) => (
                      <strong style={{ color: "#000000" }} {...props}>
                        {children}
                      </strong>
                    ),
                    em: ({ children, ...props }) => (
                      <em style={{ color: "#000000" }} {...props}>
                        {children}
                      </em>
                    ),
                    li: ({ children, ...props }) => (
                      <li style={{ color: "#000000" }} {...props}>
                        {children}
                      </li>
                    ),
                  }}
                >
                  {currentCard.back_text}
                </CustomMarkdown>
              </Box>
            </Box>
          </Box>
        </motion.div>
      </Box>

      {/* Navigation */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          mt: 2,
        }}
      >
        <IconButton
          onClick={handlePrev}
          disabled={currentCardIndex === 0}
          sx={{
            ...styles.navButton,
            opacity: currentCardIndex === 0 ? 0.5 : 1,
          }}
        >
          <NavigateBeforeIcon />
        </IconButton>

        <Typography
          variant="body2"
          sx={{
            ...styles.counter,
            fontFamily: '"Red Hat Display", sans-serif',
          }}
        >
          {currentCardIndex + 1}/{totalCards}
        </Typography>

        <IconButton
          onClick={handleNext}
          disabled={currentCardIndex === totalCards - 1}
          sx={{
            ...styles.navButton,
            opacity: currentCardIndex === totalCards - 1 ? 0.5 : 1,
          }}
        >
          <NavigateNextIcon />
        </IconButton>
      </Box>

      {!embedded && (
        <ReactionAndFavorite
          currentReaction={currentReaction}
          isSaved={isSaved}
          markedIds={markedIds}
          isTags={isTags}
          handleReactionClick={handleReactionClick}
          handleAddToFavorite={handleAddToFavorite}
          handleRemoveTag={handleRemoveTag}
        />
      )}
    </Box>
  );
};
